/**
 * SSO API Service
 *
 * API methods for SSO operations including provider lookup, authorization,
 * callback handling, and provider configuration management.
 *
 * Public endpoints (no auth): getProviders, authorize, callback
 * Protected endpoints (auth required): CRUD operations on provider configs
 */

import apiClient from './client';
import type { ApiSuccessResponse, ApiCollectionResponse, ApiActionResponse } from '../types/api.types';
import type {
  SSOTenantInfo,
  SSOAuthorizeRequest,
  SSOAuthorizeResponse,
  SSOCallbackRequest,
  SSOCallbackResponse,
  SSOProviderConfig,
  SSOProviderCreateDTO,
  SSOProviderUpdateDTO,
  SSOTestConnectionResponse,
} from '../types/sso.types';

// ============================================================================
// SSO SERVICE
// ============================================================================

export const ssoService = {
  // ==========================================================================
  // PUBLIC ENDPOINTS (no auth required)
  // ==========================================================================

  /**
   * Get SSO providers for a tenant (public, used on login page)
   * @param tenantCode - Tenant code
   */
  getProviders: async (tenantCode: string): Promise<SSOTenantInfo> => {
    const response = await apiClient.get<ApiSuccessResponse<SSOTenantInfo>>(
      `/auth/sso/providers/${tenantCode}`
    );
    return response.data.data;
  },

  /**
   * Initiate SSO authorization flow
   * Returns the IdP authorization URL to redirect the user to
   */
  authorize: async (data: SSOAuthorizeRequest): Promise<SSOAuthorizeResponse> => {
    const response = await apiClient.post<ApiSuccessResponse<SSOAuthorizeResponse>>(
      '/auth/sso/authorize',
      data
    );
    return response.data.data;
  },

  /**
   * Handle SSO callback after IdP redirect
   * Exchanges authorization code for tokens
   */
  callback: async (data: SSOCallbackRequest): Promise<SSOCallbackResponse> => {
    const response = await apiClient.post<ApiSuccessResponse<SSOCallbackResponse>>(
      '/auth/sso/callback',
      data
    );
    return response.data.data;
  },

  // ==========================================================================
  // PROTECTED ENDPOINTS (auth required, tenant admin)
  // ==========================================================================

  /**
   * List all SSO provider configurations for a tenant
   * @param tenantId - Tenant ID
   */
  listProviderConfigs: async (tenantId: string): Promise<SSOProviderConfig[]> => {
    const response = await apiClient.get<ApiCollectionResponse<SSOProviderConfig>>(
      `/tenants/${tenantId}/sso/providers`
    );
    return response.data.data;
  },

  /**
   * Get a specific SSO provider configuration
   * @param tenantId - Tenant ID
   * @param providerId - Provider ID
   */
  getProviderConfig: async (tenantId: string, providerId: string): Promise<SSOProviderConfig> => {
    const response = await apiClient.get<ApiSuccessResponse<SSOProviderConfig>>(
      `/tenants/${tenantId}/sso/providers/${providerId}`
    );
    return response.data.data;
  },

  /**
   * Create a new SSO provider configuration
   * @param tenantId - Tenant ID
   * @param data - Provider creation data
   */
  createProviderConfig: async (tenantId: string, data: SSOProviderCreateDTO): Promise<SSOProviderConfig> => {
    const response = await apiClient.post<ApiSuccessResponse<SSOProviderConfig>>(
      `/tenants/${tenantId}/sso/providers`,
      data
    );
    return response.data.data;
  },

  /**
   * Update an SSO provider configuration
   * @param tenantId - Tenant ID
   * @param providerId - Provider ID
   * @param data - Provider update data
   */
  updateProviderConfig: async (
    tenantId: string,
    providerId: string,
    data: SSOProviderUpdateDTO
  ): Promise<SSOProviderConfig> => {
    const response = await apiClient.put<ApiSuccessResponse<SSOProviderConfig>>(
      `/tenants/${tenantId}/sso/providers/${providerId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete an SSO provider configuration
   * @param tenantId - Tenant ID
   * @param providerId - Provider ID
   */
  deleteProviderConfig: async (tenantId: string, providerId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<ApiActionResponse>(
      `/tenants/${tenantId}/sso/providers/${providerId}`
    );
    return { message: response.data.message };
  },

  /**
   * Test an SSO provider connection
   * @param tenantId - Tenant ID
   * @param providerId - Provider ID
   */
  testProviderConnection: async (
    tenantId: string,
    providerId: string
  ): Promise<SSOTestConnectionResponse> => {
    const response = await apiClient.post<ApiSuccessResponse<SSOTestConnectionResponse>>(
      `/tenants/${tenantId}/sso/providers/${providerId}/test`
    );
    return response.data.data;
  },
};

export default ssoService;
